import { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery, type UseQueryOptions } from "@tanstack/react-query"

import { StorageCache } from "@be-miniapp/platform"

import { getAcceptableData } from "../utils/cache-data"

interface AggressiveCacheOptions<T> {
  cacheKey: string
  queryKey: readonly unknown[]
  queryFn: () => Promise<T>
  ttl?: number
  staleTime?: number
  refetchOnWindowFocus?: boolean
  refetchOnMount?: boolean
  refetchInterval?: number
  enabled?: boolean
  retry?: number | boolean
  retryDelay?: number
  shouldCache?: (data: T) => boolean
}

interface AggressiveCacheReturn<T> {
  data: T | null
  isLoading: boolean
  isStale: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => Promise<void>
}

class UnacceptableCacheDataError extends Error {
  constructor(cacheKey: string) {
    super(`Response rejected for cache key: ${cacheKey}`)
    this.name = "UnacceptableCacheDataError"
  }
}

/**
 * Aggressive caching hook that prioritizes cached data over API calls
 *
 * Strategy:
 * 1. Immediately return cached data from platform.storage if available
 * 2. Start background fetch for fresh data
 * 3. Update cache and component state when fresh data arrives
 * 4. Implement stale-while-revalidate pattern
 */
export function useAggressiveCache<T>({
  cacheKey,
  queryKey,
  queryFn,
  ttl = 30 * 60 * 1000, // 30 minutes
  staleTime = 5 * 60 * 1000, // 5 minutes
  refetchOnWindowFocus = true,
  refetchOnMount = false,
  refetchInterval,
  enabled = true,
  retry = 3,
  retryDelay,
  shouldCache,
}: AggressiveCacheOptions<T>): AggressiveCacheReturn<T> {
  const [cachedData, setCachedData] = useState<T | null>(null)
  const [isStale, setIsStale] = useState(false)
  const [hasLoadedStorageCache, setHasLoadedStorageCache] = useState(false)

  // Load persisted cache immediately, even when the query is temporarily disabled.
  useEffect(() => {
    let cancelled = false

    const loadCachedData = async () => {
      const cached = await StorageCache.get<T>(cacheKey)
      if (cancelled) return

      if (!cached) {
        setHasLoadedStorageCache(true)
        return
      }

      if (shouldCache && !shouldCache(cached)) {
        await StorageCache.remove(cacheKey)
        setHasLoadedStorageCache(true)
        return
      }

      setCachedData(cached)
      setHasLoadedStorageCache(true)

      const stale = await StorageCache.isStale(cacheKey, staleTime)
      if (!cancelled) {
        setIsStale(stale)
      }
    }

    loadCachedData()

    return () => {
      cancelled = true
    }
  }, [cacheKey, shouldCache, staleTime])

  const acceptableCachedData = useMemo(
    () => getAcceptableData(cachedData, shouldCache),
    [cachedData, shouldCache]
  )

  // Background query for fresh data
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const freshData = await queryFn()
      const canCache = shouldCache ? shouldCache(freshData) : true

      if (canCache) {
        await StorageCache.set(cacheKey, freshData, { ttl })
        setCachedData(freshData)
        setIsStale(false)
        return freshData
      }

      await StorageCache.remove(cacheKey)
      setCachedData(null)
      throw new UnacceptableCacheDataError(cacheKey)
    },
    enabled: enabled && hasLoadedStorageCache,
    staleTime,
    retry,
    retryDelay,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval,
    placeholderData: acceptableCachedData || undefined,
  } as UseQueryOptions<T>)

  const acceptableQueryData = useMemo(
    () => getAcceptableData(query.data, shouldCache),
    [query.data, shouldCache]
  )

  const data = acceptableCachedData || acceptableQueryData || null

  const refetch = useCallback(async () => {
    setIsStale(true)
    await query.refetch()
  }, [query])

  return {
    data,
    isLoading:
      !hasLoadedStorageCache ||
      (enabled && !data && (query.isLoading || query.isFetching)),
    isStale,
    isFetching: query.isFetching,
    error: query.error,
    refetch,
  }
}

/**
 * Simplified version for immediate cache-first behavior
 */
export function useCacheFirst<T>({
  cacheKey,
  queryKey,
  queryFn,
  ttl = 30 * 60 * 1000,
  enabled = true,
  retry = 3,
  retryDelay,
  shouldCache,
}: Omit<
  AggressiveCacheOptions<T>,
  "staleTime" | "refetchOnWindowFocus" | "refetchInterval" | "refetchOnMount"
>) {
  return useAggressiveCache({
    cacheKey,
    queryKey,
    queryFn,
    ttl,
    staleTime: ttl, // Consider data stale only after TTL
    refetchOnWindowFocus: false,
    enabled,
    retry,
    retryDelay,
    shouldCache,
  })
}

export { getAcceptableData, UnacceptableCacheDataError }
